import React, { useEffect, useState } from 'react';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Menu,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  Typography,
  IconButton,
  Tooltip,
  Stack,
  Divider,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import AddIcon from '@mui/icons-material/Add';
import EvolutionTimelineModal from '../evolutions/evolutionTimelineModal';
import EvolutionModal from '../evolutions/evolutionModal';
import SuccessMessage from '../../../../../components/successMessage';
import ErrorMessage from '../../../../../components/errorMessage';
import { useProcedures } from '../hooks/useProcedures';
import { useProcedureActions } from '../hooks/useProcedureActions';
import { useClinic } from '../../../../../hooks/useClinic';
import Loading from '../../../../../components/loading';
import ProcedureModalToOdonto from '../procedureModalToOdonto';
import ProcedureModalToAesthetic from '../procedureModalToAesthetic';
import RectificationHistoryModal from '../rectification/rectificationHistoryModal';
import { isApproved } from '../../treatmentStatus';

export default function ProcedureListMobile({ treatment, onProcedureSaved }) {
  const [isLoading, setLoading] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const [procedures, setProcedures] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedProcedure, setSelectedProcedure] = useState(null);
  const [isOdontoModalOpen, setIsOdontoModalOpen] = useState(false);
  const [isAestheticModalOpen, setIsAestheticModalOpen] = useState(false);
  const [isTypeSelectionOpen, setIsTypeSelectionOpen] = useState(false);
  const [isTimelineModalOpen, setTimelineModalOpen] = useState(false);
  const [isEvolutionModalOpen, setEvolutionModalOpen] = useState(false);
  const [isRectifyModalOpen, setRectifyModalOpen] = useState(false);
  const [isRectificationHistoryOpen, setRectificationHistoryOpen] = useState(false);

  const {
    procedures: fetchedProcedures,
    isLoading: isProceduresLoading,
    refetch: getProceduresFromTreatment,
  } = useProcedures(treatment?.treatmentId);
  const { remove, finish: finishRequest } = useProcedureActions();
  const { clinic } = useClinic();
  const modules = clinic?.modules ?? [];

  useEffect(() => {
    setProcedures(fetchedProcedures);
  }, [fetchedProcedures]);

  const hasOdonto = modules.includes('ODONTO');
  const hasAesthetic = modules.includes('AESTHETIC');

  const openProcedureModal = () => {
    if (hasOdonto && hasAesthetic) {
      setIsTypeSelectionOpen(true);
    } else if (hasAesthetic) {
      setIsAestheticModalOpen(true);
    } else {
      setIsOdontoModalOpen(true);
    }
  };

  const closeOdontoModal = () => setIsOdontoModalOpen(false);
  const closeAestheticModal = () => setIsAestheticModalOpen(false);

  const openOdontoFromSelection = () => {
    setIsTypeSelectionOpen(false);
    setIsOdontoModalOpen(true);
  };

  const openAestheticFromSelection = () => {
    setIsTypeSelectionOpen(false);
    setIsAestheticModalOpen(true);
  };

  const onSaveSuccess = (message) => {
    setShowSuccessMessage(true);
    setSuccessMessage(message);
    getProceduresFromTreatment(treatment);
    closeOdontoModal();
    closeAestheticModal();
    onProcedureSaved();
  };

  const openActionsMenu = (event, procedure) => {
    setAnchorEl(event.currentTarget);
    setSelectedProcedure(procedure);
  };

  const closeActionsMenu = () => {
    setAnchorEl(null);
  };

  const openEvolutionModal = () => {
    setEvolutionModalOpen(true);
    setAnchorEl(null);
  };

  const onSaveEvolution = (returnFromApi) => {
    if (returnFromApi.data) {
      setShowSuccessMessage(true);
      setSuccessMessage(returnFromApi.data);
      getProceduresFromTreatment(treatment);
    } else {
      setShowErrorMessage(true);
      setErrorMessage(returnFromApi.response?.data);
    }
    setEvolutionModalOpen(false);
  };

  function deleteSelectedProcedure() {
    closeActionsMenu();
    setLoading(true);
    remove(selectedProcedure.id)
      .then((returnFromApi) => {
        setShowSuccessMessage(true);
        setSuccessMessage(returnFromApi.data);
        getProceduresFromTreatment(treatment);
        onProcedureSaved();
        setLoading(false);
      })
      .catch((returnFromApi) => {
        setLoading(false);
        setShowErrorMessage(true);
        setErrorMessage(returnFromApi?.response?.data);
        console.error('ops! ocorreu um erro' + returnFromApi);
      });
  }

  const finishProcedure = () => {
    const procedureId = selectedProcedure.id;
    closeActionsMenu();
    setLoading(true);
    finishRequest(procedureId)
      .then((returnFromApi) => {
        setShowSuccessMessage(true);
        setSuccessMessage(returnFromApi.data);
        getProceduresFromTreatment(treatment);
        onProcedureSaved();
      })
      .catch((returnFromApi) => {
        setShowErrorMessage(true);
        setErrorMessage(returnFromApi?.response?.data);
        console.error('ops! ocorreu um erro' + returnFromApi);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const openRectifyModal = () => {
    setRectifyModalOpen(true);
    closeActionsMenu();
  };

  const onRectified = (message) => {
    setRectifyModalOpen(false);
    setShowSuccessMessage(true);
    setSuccessMessage(message);
    getProceduresFromTreatment(treatment);
    onProcedureSaved();
  };

  const viewRectifications = () => {
    setRectificationHistoryOpen(true);
    closeActionsMenu();
  };

  const viewEvolutions = () => {
    if (selectedProcedure) {
      setTimelineModalOpen(true);
    }
    closeActionsMenu();
  };

  if (!treatment) {
    return null;
  }

  return (
    <>
      <Loading isToHidden={isLoading === false && isProceduresLoading === false} />
      {showSuccessMessage && (
        <SuccessMessage
          message={successMessage}
          show={showSuccessMessage}
          setShow={setShowSuccessMessage}
        />
      )}
      {showErrorMessage && (
        <ErrorMessage
          message={errorMessage}
          show={showErrorMessage}
          setShow={setShowErrorMessage}
        />
      )}

      <Card variant="outlined" sx={{ borderRadius: 2 }}>
        <CardHeader
          title={
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              Procedimentos
            </Typography>
          }
          action={
            <Tooltip
              title={
                isApproved(treatment)
                  ? 'Não é possível adicionar procedimentos a um tratamento aprovado.'
                  : 'Adicionar Procedimento'
              }
            >
              <span>
                <IconButton
                  onClick={openProcedureModal}
                  disabled={isApproved(treatment)}
                  sx={{
                    backgroundColor: 'primary.main',
                    color: '#fff',
                    width: 30,
                    height: 30,
                    '&:hover': { backgroundColor: 'primary.dark' },
                    '&.Mui-disabled': {
                      backgroundColor: 'action.disabledBackground',
                      color: 'action.disabled',
                    },
                  }}
                >
                  <AddIcon sx={{ fontSize: 20 }} />
                </IconButton>
              </span>
            </Tooltip>
          }
        />
        <CardContent>
          {procedures.length === 0 ? (
            <Typography align="center" sx={{ py: 4 }}>
              Nenhum procedimento cadastrado ainda.
            </Typography>
          ) : (
            <List>
              {procedures.map((procedure) => (
                <React.Fragment key={procedure.id}>
                  <ListItem
                    secondaryAction={
                      <IconButton
                        size="small"
                        onClick={(event) => openActionsMenu(event, procedure)}
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    }
                    sx={{ alignItems: 'flex-start' }}
                  >
                    <ListItemText
                      primary={procedure.descricao}
                      primaryTypographyProps={{ variant: 'body1', fontWeight: 'bold', mb: 1 }}
                      secondary={
                        <>
                          <Typography variant="body2" color="text.primary" sx={{ mb: 1.5 }}>
                            {procedure.valor}
                          </Typography>

                          <Stack direction="row" spacing={1} alignItems="center">
                            <Chip
                              label={procedure.descricaoDoStatus}
                              color={
                                procedure.status === 'FINALIZADO'
                                  ? 'success'
                                  : procedure.status === 'EM_ANDAMENTO'
                                    ? 'warning'
                                    : 'info'
                              }
                              size="small"
                            />

                            {procedure.dentePorExtenso && (
                              <Tooltip title="Dente">
                                <Chip
                                  label={`D${procedure.dentePorExtenso}`}
                                  size="small"
                                  variant="outlined"
                                />
                              </Tooltip>
                            )}
                            {procedure.evolucoes && procedure.evolucoes.length > 0 && (
                              <Tooltip title="Quantidade de Evoluções">
                                <Chip
                                  label={`E${procedure.evolucoes.length}`}
                                  size="small"
                                  variant="outlined"
                                />
                              </Tooltip>
                            )}
                            {procedure.retificado && (
                              <Tooltip title="Procedimento retificado">
                                <Chip
                                  label="Retificado"
                                  size="small"
                                  color="warning"
                                  variant="outlined"
                                />
                              </Tooltip>
                            )}
                          </Stack>
                        </>
                      }
                    />
                  </ListItem>
                  <Divider component="li" />
                </React.Fragment>
              ))}
            </List>
          )}
        </CardContent>
      </Card>
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={closeActionsMenu}>
        <MenuItem onClick={viewEvolutions}>Ver Evoluções</MenuItem>
        <span>
          <MenuItem onClick={openEvolutionModal}>Adicionar Evolução</MenuItem>
        </span>
        <Tooltip
          placement="left"
          title={
            selectedProcedure?.status !== 'EM_ANDAMENTO'
              ? 'Só é possível retificar procedimentos em andamento.'
              : ''
          }
        >
          <span>
            <MenuItem
              data-cy="rectify-procedure-btn"
              onClick={openRectifyModal}
              disabled={selectedProcedure?.status !== 'EM_ANDAMENTO'}
            >
              Retificar
            </MenuItem>
          </span>
        </Tooltip>
        {selectedProcedure?.retificado && (
          <MenuItem data-cy="see-rectifications-btn" onClick={viewRectifications}>
            Ver Retificações
          </MenuItem>
        )}
        <span>
          <MenuItem onClick={finishProcedure} disabled={selectedProcedure?.status === 'FINALIZADO'}>
            Finalizar
          </MenuItem>
        </span>
        <MenuItem onClick={deleteSelectedProcedure} sx={{ color: 'error.main' }}>
          Deletar
        </MenuItem>
      </Menu>

      <Dialog open={isTypeSelectionOpen} onClose={() => setIsTypeSelectionOpen(false)}>
        <DialogTitle>Tipo de Procedimento</DialogTitle>
        <DialogContent>
          <Typography>Selecione o tipo de procedimento que deseja cadastrar:</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsTypeSelectionOpen(false)}>Cancelar</Button>
          <Button variant="outlined" onClick={openOdontoFromSelection}>
            Odontológico
          </Button>
          <Button variant="contained" onClick={openAestheticFromSelection}>
            Estético
          </Button>
        </DialogActions>
      </Dialog>

      <ProcedureModalToOdonto
        isOpen={isOdontoModalOpen}
        onClose={closeOdontoModal}
        onSaveSuccess={onSaveSuccess}
        treatment={treatment}
      />

      <ProcedureModalToAesthetic
        isOpen={isAestheticModalOpen}
        onClose={closeAestheticModal}
        onSaveSuccess={onSaveSuccess}
        treatment={treatment}
      />

      <EvolutionTimelineModal
        open={isTimelineModalOpen}
        onClose={() => setTimelineModalOpen(false)}
        procedure={selectedProcedure}
      />

      <EvolutionModal
        open={isEvolutionModalOpen}
        onClose={() => setEvolutionModalOpen(false)}
        onSaveEvolution={onSaveEvolution}
        selectedProcedure={selectedProcedure}
      />

      {isRectifyModalOpen && (
        <ProcedureModalToOdonto
          isOpen
          onClose={() => setRectifyModalOpen(false)}
          onSaveSuccess={onRectified}
          treatment={treatment}
          procedureToRectify={selectedProcedure}
        />
      )}

      {isRectificationHistoryOpen && (
        <RectificationHistoryModal
          open
          onClose={() => setRectificationHistoryOpen(false)}
          procedure={selectedProcedure}
        />
      )}
    </>
  );
}
